import { useCallback, useEffect, useRef, useState } from 'react';
import { TimeUpdateEvent } from '@u-wave/react-vimeo';
import { useVideoFullscreen } from '../../contexts/VideoFullscreenContext';
import PlayLogService, { PlayLogEntityType, PlayLogProgressData } from 'src/services/PlayLogService';
import {
  Container,
  HorizontalThumbnail,
  VideoFrame,
} from './styles';

type VimeoPlayerHandle = {
  on: (event: 'fullscreenchange', callback: (data: { fullscreen: boolean }) => void) => void;
  off: (event: 'fullscreenchange') => void;
};

const COMPLETED_VIDEO_TIME_POINT = 0.8;
const DEFAULT_QUALITY = '540p';
const PROGRESS_SYNC_INTERVAL_MS = 5000;

const VimeoPlayer: React.FC<{
  className?: string;
  viewLink?: string;
  horizontalThumbnail?: string | null;
  isCompleted?: boolean;
  onComplete?: () => void;
  onEnd?: () => void;
  entityId?: string;
  entityType?: PlayLogEntityType;
}> = ({
  className,
  viewLink,
  horizontalThumbnail,
  isCompleted,
  onComplete,
  onEnd,
  entityId,
  entityType,
}) => {
  const playerRef = useRef<VimeoPlayerHandle | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { setVideoFullscreen } = useVideoFullscreen();
  const shouldFireOnComplete = useRef(!isCompleted);
  const isCreatingPlayLog = useRef(false);
  const accumulatedSeconds = useRef(0);
  const lastTimeUpdateAt = useRef<number | null>(null);
  const maxPercent = useRef(0);
  const videoDuration = useRef<number | undefined>(undefined);
  const [playLogId, setPlayLogId] = useState<string | null>(null);
  const playLogIdRef = useRef<string | null>(null);
  const unmountedRef = useRef(false);

  // `completed` is sent with every sync past the threshold (not just once):
  // the backend assignment is idempotent, and a one-shot send could be lost
  // to a single failed request.
  const buildPayload = useCallback((endedAt?: string): PlayLogProgressData => ({
    watchSeconds: Math.round(accumulatedSeconds.current),
    maxPercent: maxPercent.current,
    ...(maxPercent.current >= COMPLETED_VIDEO_TIME_POINT && { completed: true }),
    ...(endedAt && { endedAt }),
  }), []);

  const buildPayloadRef = useRef(buildPayload);
  buildPayloadRef.current = buildPayload;

  useEffect(() => {
    if (!playLogId) return;
    const timer = setInterval(() => {
      PlayLogService.update(playLogId, buildPayload()).catch(() => {});
    }, PROGRESS_SYNC_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [playLogId, buildPayload]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      const id = playLogIdRef.current;
      if (id) PlayLogService.update(id, buildPayloadRef.current(new Date().toISOString()));
    };
  }, []);

  // React cleanup never runs on a hard unload (tab close, refresh) — flush
  // the final state via keepalive so it survives the page teardown.
  useEffect(() => {
    const handlePageHide = () => {
      const id = playLogIdRef.current;
      if (id) PlayLogService.flush(id, buildPayloadRef.current(new Date().toISOString()));
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);

  const handleTimeUpdate = useCallback((playerData: TimeUpdateEvent) => {
    if (videoDuration.current === undefined && playerData.duration) {
      videoDuration.current = playerData.duration;
    }

    // The guard is a ref set synchronously BEFORE any state update: two
    // timeupdate events can arrive within one render cycle, and a guard
    // living inside a setState updater would let both of them create a log.
    if (!isCreatingPlayLog.current && !playLogIdRef.current && entityId && entityType) {
      isCreatingPlayLog.current = true;
      PlayLogService.create(entityId, entityType, videoDuration.current)
        .then(({ id }) => {
          playLogIdRef.current = id;
          if (unmountedRef.current) {
            // The player was already unmounted (e.g. user scrubbed to the
            // end and navigated away before this request resolved) — the
            // periodic sync and unmount cleanup won't fire again for this
            // id, so send the final state now or it's lost forever.
            PlayLogService.update(id, buildPayloadRef.current(new Date().toISOString())).catch(() => {});
            return;
          }
          setPlayLogId(id);
        })
        .catch(() => { isCreatingPlayLog.current = false; });
    }

    const prev = lastTimeUpdateAt.current;
    const curr = playerData.seconds;
    if (prev !== null) {
      const delta = curr - prev;
      if (delta > 0 && delta < 5) {
        accumulatedSeconds.current += delta;
      }
    }
    lastTimeUpdateAt.current = curr;
    maxPercent.current = Math.max(maxPercent.current, playerData.percent);

    if (shouldFireOnComplete.current && playerData.percent >= COMPLETED_VIDEO_TIME_POINT && onComplete) {
      onComplete();
      shouldFireOnComplete.current = false;
    }
  }, [entityId, entityType, onComplete]);

  const handlePause = useCallback(() => {
    lastTimeUpdateAt.current = null;
    if (playLogId) PlayLogService.update(playLogId, buildPayload());
  }, [playLogId, buildPayload]);

  const handleEnd = useCallback(() => {
    lastTimeUpdateAt.current = null;
    if (playLogId) PlayLogService.update(playLogId, buildPayload(new Date().toISOString()));

    // A play log spans exactly one playthrough. Without this reset, replaying
    // the video without leaving the page would keep accumulating into the same
    // log — minutes would sum correctly, but the completion could only ever
    // count once no matter how many times the video was watched.
    playLogIdRef.current = null;
    isCreatingPlayLog.current = false;
    accumulatedSeconds.current = 0;
    maxPercent.current = 0;
    setPlayLogId(null);

    onEnd?.();
  }, [playLogId, buildPayload, onEnd]);

  const handleReady = useCallback((player: VimeoPlayerHandle) => {
    playerRef.current = player;

    player.on('fullscreenchange', (data: { fullscreen: boolean }) => {
      setIsFullscreen(data.fullscreen);
      setVideoFullscreen(data.fullscreen);
    });
  }, [setVideoFullscreen]);

  useEffect(() => {
    return () => {
      playerRef.current?.off('fullscreenchange');
      setVideoFullscreen(false);
    };
  }, [setVideoFullscreen]);

  return (
    <Container className={className} $isFullscreen={isFullscreen}>
      {viewLink && (
        <VideoFrame
          key={viewLink}
          video={viewLink}
          onReady={handleReady}
          onTimeUpdate={handleTimeUpdate}
          onPause={handlePause}
          onEnd={handleEnd}
          quality={DEFAULT_QUALITY}
          playsInline
        />
      )}
      {!viewLink && horizontalThumbnail && (
        <HorizontalThumbnail src={horizontalThumbnail} />
      )}
    </Container>
  );
};

export default VimeoPlayer;
